import { usePage } from "@inertiajs/react"
import { CreditCard } from "lucide-react"
import { UserLayout } from "@/components/layouts/user-layout"
import { ScreenHeader } from "@/components/ui/headers"
import { t } from "@/lib/i18n"

type PaymentMethodListItem = {
  id: number
}

type Props = {
  paymentMethods: PaymentMethodListItem[]
}

/**
 * Saved cards, for registered diners only — a guest is never asked for card
 * details, so they never reach this screen.
 *
 * This change ships the shell. Saving a card is not a small addition: Menny
 * charges directly on each restaurant's connected Stripe account, which means a
 * card saved at one restaurant is not chargeable at another. Making one card
 * work everywhere takes a platform-level customer and a payment method cloned
 * into the connected account at charge time. That is its own change.
 */
export default function UserProfilesPaymentMethods() {
  const { paymentMethods } = usePage<Props>().props

  return (
    <UserLayout>
      <div className="text-foreground" data-testid="payment-methods-view">
        <ScreenHeader title={t("ui.react.pages.user.payment_methods.index.title")} />

        {paymentMethods.length === 0 && <EmptyState />}
      </div>
    </UserLayout>
  )
}

function EmptyState() {
  return (
    <div
      data-testid="payment-methods-empty"
      className="flex flex-col items-center gap-3 px-6 py-16 text-center text-muted-foreground"
    >
      <span className="flex size-14 items-center justify-center rounded-full bg-muted">
        <CreditCard className="size-6" />
      </span>
      <p className="text-sm font-medium">
        {t("ui.react.pages.user.payment_methods.index.empty_title")}
      </p>
      <p className="max-w-64 text-xs">
        {t("ui.react.pages.user.payment_methods.index.empty_hint")}
      </p>
    </div>
  )
}
